import { useState, useCallback, useEffect } from 'react';

/**
 * useSavedLayouts — localStorage-backed saved camera layouts for MCPlayback (2e). A layout is a named
 * snapshot of the current slots (the cams + their order). One-tap restore from the camera drawer.
 *
 * LOCKED storage model (David 2026-06-18): localStorage, **keyed by `{serial, user}`** so multiple users
 * on one shared workstation never cross-contaminate and the visible set scopes to the active NVR serial
 * (`mcpbSavedLayouts__{serial}__{user}`). The hook is the single swappable client module — a cloud-DB
 * backend is a separate POST-V1 plan (plan §11 v2). Caps per Q10: soft-warn at 15, hard cap at 50.
 *
 * The pure list ops are exported so they can be unit-tested without a hook renderer (jsdom can't run
 * renderHook in this CRA setup).
 */

export const SOFT_CAP = 15;
export const HARD_CAP = 50;

export const layoutsKey = (serial, user) => `mcpbSavedLayouts__${serial != null ? serial : 'x'}__${user || 'x'}`;

const readLayouts = (key) => {
  try {
    const raw = JSON.parse(localStorage.getItem(key));
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
};
const writeLayouts = (key, list) => {
  try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* quota/serialization — ignore */ }
};

// Monotonic-ish id without relying only on Date.now (which can collide on rapid saves).
let _seq = 0;
const newLayoutId = () => `L${Date.now().toString(36)}${(_seq++).toString(36)}`;

// A layout stores only {bSerial,bCamera,sName} per filled slot (drop empties + any extra cam fields).
const normalizeSlots = (slots) =>
  (slots || []).filter(Boolean).map((c) => ({ bSerial: c.bSerial, bCamera: c.bCamera, sName: c.sName }));

/** Pure: append a layout (or refuse at the hard cap). Returns { list, ok, reason?, warn }. */
export const addLayout = (list, name, slots, idGen = newLayoutId) => {
  if (list.length >= HARD_CAP) return { list, ok: false, reason: 'cap', warn: false };
  const layout = {
    id: idGen(),
    name: (name || 'Untitled').trim() || 'Untitled',
    slots: normalizeSlots(slots),
    createdAt: Date.now(),
  };
  const next = [...list, layout];
  return { list: next, ok: true, layout, warn: next.length >= SOFT_CAP };
};
export const renameInList = (list, id, name) =>
  list.map((l) => (l.id === id ? { ...l, name: (name || l.name).trim() || l.name } : l));
// Replace a layout's cam set with the current slots (id/name/createdAt preserved — an overwrite, not a new save).
export const replaceInList = (list, id, slots) =>
  list.map((l) => (l.id === id ? { ...l, slots: normalizeSlots(slots) } : l));
export const removeFromList = (list, id) => list.filter((l) => l.id !== id);

export const useSavedLayouts = ({ serial, user } = {}) => {
  const key = layoutsKey(serial, user);
  const [layouts, setLayouts] = useState(() => readLayouts(key));

  // Re-read when the scope key changes (jump to another NVR / different user on the workstation).
  useEffect(() => { setLayouts(readLayouts(key)); }, [key]);

  const persist = useCallback((next) => { writeLayouts(key, next); setLayouts(next); }, [key]);

  const saveLayout = useCallback((name, slots) => {
    const res = addLayout(readLayouts(key), name, slots);
    if (res.ok) persist(res.list);
    return res; // { ok, reason?, warn, layout? }
  }, [key, persist]);

  const renameLayout = useCallback((id, name) => persist(renameInList(readLayouts(key), id, name)), [key, persist]);
  const replaceLayout = useCallback((id, slots) => persist(replaceInList(readLayouts(key), id, slots)), [key, persist]);
  const deleteLayout = useCallback((id) => persist(removeFromList(readLayouts(key), id)), [key, persist]);

  return {
    layouts,
    saveLayout,
    renameLayout,
    replaceLayout,
    deleteLayout,
    atCap: layouts.length >= HARD_CAP,
    nearCap: layouts.length >= SOFT_CAP,
  };
};
